import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Illustration,
  type IllustrationKind,
  SkeletonLoader,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_BLD, CF_NEU_STL } from '../../../../shared/constants';

import './ServiceIdentityListShared.css';

interface InitialLoadingProps {
  count?: number;
}

/**
 * Full-page skeleton loader shown during the initial agent list fetch.
 * @param props - {@link InitialLoadingProps}
 * @param props.count - Number of header skeleton bars to render. Defaults to `4`.
 * @returns A skeleton placeholder layout.
 */
export function InitialLoading({ count = 4 }: InitialLoadingProps) {
  return (
    <div className='InitialLoading'>
      <div className='InitialLoading-skeletonGrid'>
        {Array.from({ length: count }, (_, i) => (
          <SkeletonLoader
            key={i}
            shape='square'
            animationStyle='pulse'
            height='100%'
            width='100%'
          />
        ))}
      </div>
      <SkeletonLoader
        shape='square'
        animationStyle='pulse'
        height='5rem'
        width='100%'
      />
      <SkeletonLoader
        shape='square'
        animationStyle='pulse'
        height='100%'
        width='100%'
      />
    </div>
  );
}

InitialLoading.defaultProps = {
  count: 4,
};

interface InitialErrorProps {
  onRefresh: () => void;
}

/**
 * Full-page error state with an illustration and a Refresh button, shown when the initial agent list fetch fails.
 * @param props - {@link InitialErrorProps}
 * @param props.onRefresh - Callback invoked when the user clicks Refresh.
 * @returns An error illustration with a Refresh button.
 */
export function InitialError({ onRefresh }: InitialErrorProps) {
  return (
    <div className='InitialError'>
      <Illustration kind={'Error' as IllustrationKind} />
      <div className='InitialError-textWrapper'>
        <Typography kind='h1' color={CF_NEU_BLD}>
          <FormattedMessage
            id='FRGlPI'
            defaultMessage='Something went wrong while loading the page.'
            description='Something went wrong while loading the page.'
          />
        </Typography>
        <Typography kind='body1' color={CF_NEU_BLD}>
          <FormattedMessage
            id='gAO6kt'
            defaultMessage='Try refreshing, or check back later. If the issue persists, contact your administrator.'
            description='Try refreshing, or check back later. If the issue persists, contact your administrator.'
          />
        </Typography>
      </div>
      <Button kind='filled' onClick={onRefresh}>
        <FormattedMessage
          id='iqkpnY'
          defaultMessage='Refresh'
          description='Refresh'
        />
      </Button>
    </div>
  );
}

/**
 * Empty state shown when no service identities exist yet.
 * @returns An empty-state message.
 */
export function EmptyList() {
  return (
    <div className='EmptyList'>
      <Typography kind='body1' color={CF_NEU_STL}>
        <FormattedMessage
          id='0EcpaW'
          defaultMessage='Nothing to see yet—this space will populate as identities are onboarded.'
          description='Empty list message for service identities'
        />
      </Typography>
    </div>
  );
}

/**
 * Empty state shown when the current search/filter combination returns no agents. Suggests updating filters or clearing the search.
 * @returns An empty-state illustration with filter guidance text.
 */
export function EmptyListWithFilters() {
  return (
    <div className='EmptyListWithFilters'>
      <Illustration kind={'Empty' as IllustrationKind} />
      <div className='EmptyListWithFilters-textWrapper'>
        <Typography kind='h1' color={CF_NEU_BLD}>
          <FormattedMessage
            id='JKdOCX'
            defaultMessage='No Matching Results Found'
            description='Header for empty list state when search or filters yield no results'
          />
        </Typography>
        <Typography kind='body1' color={CF_NEU_BLD}>
          <FormattedMessage
            id='WfJM32'
            defaultMessage='Try updating the filters, clearing search or check back later.'
            description='Guidance for user when search or filters yield no results in service identities list'
          />
        </Typography>
      </div>
    </div>
  );
}

/**
 * Skeleton loader appended below the list while the next page of agents is being fetched (infinite scroll).
 * @returns A set of skeleton rows for the next page.
 */
export function NextPageLoader() {
  return (
    <div className='NextPageLoader'>
      {Array.from({ length: 3 }, (_, i) => (
        <SkeletonLoader
          key={i}
          shape='square'
          animationStyle='wave'
          height='4rem'
          width='100%'
        />
      ))}
    </div>
  );
}

interface NextPageErrorProps {
  onNextRefresh: () => void;
}

/**
 * Inline error state shown when a subsequent page fetch fails during infinite scroll. Displays an error illustration with a Refresh button.
 * @param props - {@link NextPageErrorProps}
 * @param props.onNextRefresh - Callback invoked when the user clicks Refresh.
 * @returns An inline error illustration with a Refresh button.
 */
export function NextPageError({ onNextRefresh }: NextPageErrorProps) {
  return (
    <div className='NextPageError'>
      <Illustration kind={'Error' as IllustrationKind} />
      <Typography kind='h1' color={CF_NEU_BLD}>
        <FormattedMessage
          id='FRGlPI'
          defaultMessage='Something went wrong while loading the page.'
          description='Something went wrong while loading the page.'
        />
      </Typography>
      <Typography kind='body1' color={CF_NEU_BLD}>
        <FormattedMessage
          id='gAO6kt'
          defaultMessage='Try refreshing, or check back later. If the issue persists, contact your administrator.'
          description='Try refreshing, or check back later. If the issue persists, contact your administrator.'
        />
      </Typography>
      <Button type='button' kind='filled' onClick={onNextRefresh}>
        <FormattedMessage
          id='iqkpnY'
          defaultMessage='Refresh'
          description='Refresh'
        />
      </Button>
    </div>
  );
}
